import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { CreatePOPayload } from 'src/app/interfaces/purchase-order.interface';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { ToasterService } from 'src/app/services/core/toaster.service';

@Component({
    selector: 'app-confirm-purchase-order',
    templateUrl: './confirm-purchase-order.component.html',
    styleUrls: ['./confirm-purchase-order.component.scss'],
    standalone: false
})
export class ConfirmPurchaseOrderComponent implements OnInit {
  vendorPayloadList: CreatePOPayload[] = []
  jobIdList: any[] = []
  vendorGroupList: any[] = []
  purchaseOrderObject: any;
  panelStates: boolean[] = [];
  isPONumberGenerated: boolean = false;
  priceEngineeringFlag: boolean = false;
  isEditMode: boolean = false;
  purchaseOrderId: number | null = null;
  deselectedPOItems: any[] = [];
  materialList: any[] = [];
  isProcessing: boolean = false;


  constructor(public dialogRef: MatDialogRef<ConfirmPurchaseOrderComponent>,
    public backApiService: BackendApiService,
    public dialog: NgDialogAnimationService,
    public toasterService: ToasterService,
    public router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    this.vendorGroupList = data.info
    this.jobIdList = data.jobIdList
    this.purchaseOrderObject = data.purchaseOrderObject,
      this.priceEngineeringFlag = data.priceEngineeringFlag;
    this.isEditMode = data.isEditMode || false;
    this.purchaseOrderId = data.purchaseOrderId || null;
    this.deselectedPOItems = data.deselectedPOItems || [];
    this.materialList = data.materialList || [];
  }

  ngOnInit(): void {
    // Clone the data safely (deep copy)
    this.vendorGroupList = (this.data?.info || []).map((vendor: any) => ({
      ...vendor,
      isExpand: false, // collapsed by default
      materialList: Array.isArray(vendor.materialList) ? [...vendor.materialList] : []
    }));

    this.jobIdList = this.data?.jobIdList || [];
    this.purchaseOrderObject = this.data?.purchaseOrderObject;

    if (this.jobIdList?.length > 0) {
      this.generatePONumber()
    }
  }

  close = (data: any = '') => {
    // If PO was successfully created and no specific data provided, emit success status
    if (this.isPONumberGenerated && !data) {
      this.dialogRef.close({ status: 'updated' });
    } else {
      this.dialogRef.close(data);
    }
  }

  confirm() {
    this.isProcessing = true;
    // Check if this is edit mode
    if (this.isEditMode && this.purchaseOrderId) {
      // Edit mode: Handle both update for existing vendor and create for new vendors
      this.handleEditModeConfirm();
    } else {
      // Create mode: Use existing functionality unchanged
      this.backApiService.postApi('purchase/orders/store', this.vendorPayloadList).subscribe((response: any) => {
        if (response?.success) {
          this.isProcessing = false;
          this.toasterService.showSuccess('Purchase Order', response?.message);
          this.isPONumberGenerated = true;

          const createdOrders = response.data;

          createdOrders.forEach((order: any) => {
            const vendorId = order.vendor.id;
            const poNumber = order.po_number;
            const poId = order.id;

            // Vendor group list ke andar matching vendor search karo
            const matchingVendors = this.vendorGroupList.filter(v => v.vendor_id === vendorId);

            matchingVendors.forEach(groupItem => {
              // Group level PO update (agar group_name match kare to)
              if (!groupItem.po_number) {
                groupItem.po_number = poNumber;
                groupItem.po_id = poId;
              }

              // Material list update
              if (order.purchase_order_items?.length) {
                order.purchase_order_items.forEach((item: any) => {
                  const matchingMat = groupItem.materialList?.find(
                    (m: any) => m.job_scope_item_id === item.job_scope_item_id
                  );
                  if (matchingMat) {
                    matchingMat.po_number = poNumber;
                    matchingMat.po_id = poId;
                    matchingMat.po_item_id = item.id;
                  }
                });
              }
            });
          });

        } else {
          this.isProcessing = false;
          this.toasterService.showError('Purchase Order', response?.message);
        }
      }, () => {
        this.isProcessing = false;
      });
    }
  }

  /**
   * Handle Edit Mode Confirmation - Update existing vendor items and create new POs for new vendors
   */
  handleEditModeConfirm() {
    // Generate PO payloads if not already done
    if (!this.vendorPayloadList || this.vendorPayloadList.length === 0) {
      this.generatePONumber();
    }

    // Separate vendor groups into existing and new
    const existingVendorGroups = this.vendorGroupList.filter((v: any) => v.isExistingVendor === true);
    const newVendorGroups = this.vendorGroupList.filter((v: any) => !v.isExistingVendor);

    let updatePromises: any[] = [];

    // Handle existing vendor update (if there are items)
    if (existingVendorGroups.length > 0 && existingVendorGroups[0].materialList.length > 0) {
      const updatePromise = this.updateExistingPurchaseOrder(existingVendorGroups[0]);
      updatePromises.push(updatePromise);
    } else if (existingVendorGroups.length > 0 && existingVendorGroups[0].materialList.length === 0) {
      // If no items for existing vendor, still update with empty items to clear the PO
      const updatePromise = this.updateExistingPurchaseOrder(existingVendorGroups[0]);
      updatePromises.push(updatePromise);
    }

    // Handle new vendor POs creation
    if (newVendorGroups.length > 0) {
      const createPromise = this.createNewPurchaseOrders(newVendorGroups);
      updatePromises.push(createPromise);
    }

    // Wait for all operations to complete
    Promise.all(updatePromises).then((results) => {
      this.isProcessing = false;
      console.log('All operations completed:', results);

      // Check if all were successful
      const allSuccessful = results.every(r => r.success);

      if (allSuccessful) {
        this.toasterService.showSuccess('Purchase Order', 'Purchase orders processed successfully!');
        this.isPONumberGenerated = true;

        // Close dialog with success status
        this.close({ status: 'updated', data: results });
      } else {
        // Some operations failed
        this.toasterService.showError('Purchase Order', 'Some operations failed.');
      }
    }).catch((error) => {
      this.isProcessing = false;
      console.error('Error processing purchase orders:', error);
      this.toasterService.showError('Purchase Order', 'Failed to process purchase orders');
    });
  }

  /**
   * Update existing Purchase Order
   */
  updateExistingPurchaseOrder(vendorGroup: any): Promise<any> {
    return new Promise((resolve, reject) => {
      // Find the corresponding payload for this vendor
      const updateData = this.vendorPayloadList.find(p => p.vendor_id === vendorGroup.vendor_id);

      if (!updateData) {
        resolve({ success: false, message: 'No update data found' });
        return;
      }

      // Map materials to update format
      updateData.items = vendorGroup.materialList.map((material: any, index: number) => {

        const unitCost = Number(material.unit_price || material.unit_cost || 0);
        const quantity = Number(material.new_quantity || material.quantity || 0);

        let contractPrice = 0;
        if (material.contract_price !== undefined && material.contract_price !== null) {
          contractPrice = Number(material.contract_price);
        } else if (material.new_quantity_total && quantity > 0) {
          contractPrice = Number(material.new_quantity_total) / quantity;
        } else {
          contractPrice = unitCost * quantity;
        }

        const totalCost = unitCost * quantity;

        const item: any = {
          quantity_on_hand: material.quantity_on_hand || 0,
          order_quantity: quantity,
          total_cost: totalCost,
          scope_id: material.scope_id || null,
          contract_cost: contractPrice,
          unit_cost: unitCost,
          po_contract_cost: this.priceEngineeringFlag
            ? Number(material.new_quantity_total)
            : Number(material.previous_quantity_total || material.new_quantity_total || material.contract_price),
          job_id: material.job_id || null,
          material_id: material.material_id || material.product_id || null,
          material_name: material.product_name || material.material_name || '',
          material_code: material.material_code || '',
          material_description: material.material_description || '',
          scope: material.scope || material.scope_name || '',
          job_number: material.job_number || '',
          // Stage 6.7: Estimated use tax only (vendor tax is at PO level)
          estimated_use_tax: material.estimated_use_tax || 0
        };

        // ✅ CRITICAL FIX: Determine job_scope_item_id
        // When creating NEW PO: material.id from API IS the job_scope_item_id
        // When editing PO: material.id is the PO item ID

        if (material.job_scope_item_id) {
          // Already has job_scope_item_id set
          item.job_scope_item_id = material.job_scope_item_id;
        } else if (!this.isEditMode && material.id) {
          // Creating NEW PO: material.id from API is actually job_scope_item_id
          item.job_scope_item_id = material.id;
        } else if (this.isEditMode) {
          // Editing existing PO: material.id is the PO item ID for update
          const poItemId = material.id ||
                          material.po_item_id ||
                          material.poItemId ||
                          material.purchase_order_item_id ||
                          material._originalPoItem?.id;

          if (poItemId) {
            item.id = poItemId;
          }
        }

        return item;
      });

      const updateEndpoint = `purchase/orders/update/${this.purchaseOrderId}`;

      this.backApiService.putApi(updateEndpoint, updateData).subscribe(
        (response: any) => {

          if (response?.success) {
            resolve({
              success: true,
              type: 'update',
              vendor: vendorGroup.vendor_name,
              poNumber: vendorGroup.existingPoNumber,
              data: response.data
            });
          } else {
            resolve({
              success: false,
              type: 'update',
              vendor: vendorGroup.vendor_name,
              message: response?.message || 'Failed to update'
            });
          }
        },
        (error: any) => {
          console.error('Error updating purchase order:', error);
          resolve({
            success: false,
            type: 'update',
            vendor: vendorGroup.vendor_name,
            message: error?.error?.message || error?.message || 'Failed to update'
          });
        }
      );
    });
  }

  /**
   * Create new Purchase Orders for new vendors
   */
  createNewPurchaseOrders(newVendorGroups: any[]): Promise<any> {
    return new Promise((resolve, reject) => {
      // Create payloads for new vendors
      const newVendorPayloads = newVendorGroups.map((vendorGroup: any) => {
        const payload = this.vendorPayloadList.find(p => p.vendor_id === vendorGroup.vendor_id);
        if (!payload) {
          // Create new payload if not found
          return this.createPayloadForVendorGroup(vendorGroup);
        }
        return payload;
      }).filter(p => p !== null);

      if (newVendorPayloads.length === 0) {
        resolve({ success: true, message: 'No new vendors to create' });
        return;
      }

      this.backApiService.postApi('purchase/orders/store', newVendorPayloads).subscribe(
        (response: any) => {

          if (response?.success) {
            // Update vendor groups with new PO numbers
            const createdOrders = response.data || [];
            createdOrders.forEach((order: any) => {
              const vendorGroup = newVendorGroups.find(v => v.vendor_id === order.vendor.id);
              if (vendorGroup) {
                vendorGroup.po_number = order.po_number;
                vendorGroup.po_id = order.id;
              }
            });

            resolve({
              success: true,
              type: 'create',
              vendors: newVendorGroups.map(v => v.vendor_name),
              poNumbers: createdOrders.map((o: any) => o.po_number),
              data: createdOrders
            });
          } else {
            resolve({
              success: false,
              type: 'create',
              message: response?.message || 'Failed to create new POs'
            });
          }
        },
        (error: any) => {
          resolve({
            success: false,
            type: 'create',
            message: error?.error?.message || error?.message || 'Failed to create new POs'
          });
        }
      );
    });
  }

  /**
   * Create payload for a vendor group
   */
  createPayloadForVendorGroup(vendorGroup: any): any {
    const unitCost = (material: any) => Number(material.unit_price || material.unit_cost || 0);
    const quantity = (material: any) => Number(material.new_quantity || material.quantity || 0);

    console.log('🔍 Creating payload for vendor group:', {
      vendor_name: vendorGroup.name || vendorGroup.vendor_name,
      has_is_free_shipping: 'is_free_shipping' in vendorGroup,
      is_free_shipping_value: vendorGroup.is_free_shipping,
      has_is_discount: 'is_discount' in vendorGroup,
      is_discount_value: vendorGroup.is_discount
    });

    const payload = {
      vendor_id: vendorGroup.vendor_id,
      po_number: '', // Will be generated by backend
      shipping_cost: vendorGroup.fee_ship || '0',
      quote_amount: vendorGroup.quote_amount || '0',
      is_free_shipping: vendorGroup.is_free_shipping ?? false,
      quote_number: String(vendorGroup.quote_number || ''),
      quotation_reference: vendorGroup.quotation_reference || vendorGroup.quote_number || '',  // NEW: Vendor quote reference
      type: this.purchaseOrderObject.type,
      price_engineering_flag: this.purchaseOrderObject.price_engineering_flag,
      savings_amount: vendorGroup.calculation?.costSaving || 0,
      cost_escalation: vendorGroup.calculation?.cost_escalation || 0,
      subtotal_cost: vendorGroup.calculation?.subTotalCost || 0,
      shipping_charges: vendorGroup.calculation?.shippingCharges || 0,
      total_cost: vendorGroup.calculation?.totalCost || 0,
      is_discount: vendorGroup.is_discount ?? false,
      group_name: vendorGroup.uniqueCode || '',
      material_cost: vendorGroup.calculation?.materialCost || 0,
      items: vendorGroup.materialList.map((material: any) => {
        const itemQuantity = quantity(material);
        const itemUnitCost = unitCost(material);
        const totalCost = itemUnitCost * itemQuantity;

        let contractPrice = 0;
        if (material.contract_price !== undefined && material.contract_price !== null) {
          contractPrice = Number(material.contract_price);
        } else if (material.new_quantity_total && itemQuantity > 0) {
          contractPrice = Number(material.new_quantity_total) / itemQuantity;
        } else {
          contractPrice = totalCost;
        }

        return {
          // For vendor-based POs (type='vendor'), job_scope_item_id should be null
          // For job-based POs, use the actual job_scope_item_id
          job_scope_item_id: material.job_scope_item_id || null,
          quantity_on_hand: material.quantity_on_hand || 0,
          order_quantity: itemQuantity,
          total_cost: totalCost,
          scope_id: material.scope_id || null,
          contract_cost: contractPrice,
          unit_cost: itemUnitCost,
          po_contract_cost: this.priceEngineeringFlag
            ? Number(material.new_quantity_total)
            : Number(material.previous_quantity_total || material.new_quantity_total || contractPrice),
          job_id: material.job_id || null,
          material_id: material.material_id || material.product_id || material.id || null,
          material_name: material.product_name || material.material_name || '',
          material_code: material.material_code || '',
          material_description: material.material_description || '',
          scope: material.scope || material.scope_name || '',
          job_number: material.job_number || ''
        };
      })
    };

    return payload;
  }

  /**
   * Update Purchase Order - Edit Mode Only (DEPRECATED - kept for backward compatibility)
   */
  updatePurchaseOrder() {
    // Prepare update payload - we need a single object for PUT, not an array
    if (!this.vendorPayloadList || this.vendorPayloadList.length === 0) {
      // Generate the payload first if not already done
      this.generatePONumber();
    }

    if (!this.vendorPayloadList || this.vendorPayloadList.length === 0) {
      this.toasterService.showError('Purchase Order', 'No data to update');
      return;
    }

    // For update, take the first vendor group (usually only one in edit mode for regular PO)
    const updateData = this.vendorPayloadList[0];

    // Add item IDs for update - map from materialList to get the original PO item IDs
    const vendorGroup = this.vendorGroupList[0];
    if (vendorGroup && vendorGroup.materialList) {
      updateData.items = vendorGroup.materialList.map((material: any) => {
        console.log('Processing material for update:', material);
        console.log('Material details - job_id:', material.job_id, 'scope_id:', material.scope_id, 'isPOItem:', material.isPOItem);
        console.log('Material pricing - contract_price:', material.contract_price, 'unit_price:', material.unit_price, 'unit_cost:', material.unit_cost);

        // Calculate the unit cost and total cost properly
        const unitCost = Number(material.unit_price || material.unit_cost || 0);
        const quantity = Number(material.new_quantity || material.quantity || 0);

        // For contract price: if user edited new_quantity_total, calculate from that
        let contractPrice = 0;
        if (material.contract_price !== undefined && material.contract_price !== null) {
          contractPrice = Number(material.contract_price);
        } else if (material.new_quantity_total && quantity > 0) {
          // User edited the total, calculate unit contract price from it
          contractPrice = Number(material.new_quantity_total) / quantity;
        } else {
          // Fallback to unit cost * quantity
          contractPrice = unitCost * quantity;
        }

        const totalCost = unitCost * quantity;

        console.log('Calculated values - unitCost:', unitCost, 'quantity:', quantity, 'contractPrice:', contractPrice, 'totalCost:', totalCost);

        const item: any = {
          quantity_on_hand: material.quantity_on_hand || 0,
          order_quantity: quantity,
          // Use the calculated total cost
          total_cost: totalCost,
          scope_id: material.scope_id || null,
          // Use the calculated contract price
          contract_cost: contractPrice,
          unit_cost: unitCost,
          po_contract_cost: this.priceEngineeringFlag
            ? Number(material.new_quantity_total)
            : Number(material.previous_quantity_total || material.new_quantity_total || material.contract_price),
          // CRITICAL: Include all necessary fields for backend
          job_id: material.job_id || null,
          // Send material_id
          material_id: material.material_id || material.product_id || null,
          material_name: material.product_name || material.material_name || '',
          material_code: material.material_code || '',
          material_description: material.material_description || '',
          scope: material.scope || material.scope_name || '',
          job_number: material.job_number || ''
        };

        // For job-based POs, include job_scope_item_id
        // For vendor-based POs, include the PO item ID
        if (material.job_scope_item_id) {
          // Job-based PO: MUST use job_scope_item_id - this is critical for the observer to work
          item.job_scope_item_id = material.job_scope_item_id;
        } else {
          // Vendor-based PO: Use the PO item ID
          // Check various possible property names for the PO item ID
          const poItemId = material.id || material.po_item_id || material.poItemId || material.purchase_order_item_id;
          if (poItemId) {
            item.id = poItemId;
          }
        }

        console.log('Final item for update - job_scope_item_id:', item.job_scope_item_id, 'id:', item.id, 'job_id:', item.job_id, 'scope_id:', item.scope_id, 'material_id:', item.material_id);

        console.log('Item to update:', item);

        return item;
      });
    }

    const updateEndpoint = `purchase/orders/update/${this.purchaseOrderId}`;

    console.log('Updating PO with ID:', this.purchaseOrderId);
    console.log('Update endpoint:', updateEndpoint);
    console.log('Update payload:', updateData);

    // Call PUT API for update
    this.backApiService.putApi(updateEndpoint, updateData).subscribe(
      (response: any) => {
        console.log('Update API Response:', response);
        console.log('Response data:', response?.data);
        console.log('Response items:', response?.data?.purchase_order_items);

        if (response?.success) {
          this.toasterService.showSuccess('Purchase Order', response?.message || 'Purchase Order updated successfully!');
          this.isPONumberGenerated = true;

          // Reset status of deselected PO items to make them available again
          if (this.deselectedPOItems && this.deselectedPOItems.length > 0 && this.materialList) {
            console.log('Resetting status for deselected PO items:', this.deselectedPOItems);

            this.deselectedPOItems.forEach((deselectedItem: any) => {
              // Find the material in the material list
              const material = this.materialList.find((mat: any) => {
                const matId = mat.material_id || mat.product_id || mat.id;
                const deselectedId = deselectedItem.material_id || deselectedItem.product_id || deselectedItem.job_scope_item_id;
                return matId === deselectedId;
              });

              if (material) {
                // Reset the PO item status to make it available
                material.isPOItem = false;
                material.isDisabled = false;
                material.available = true;
                material.isSelected = false;
                material.color = 'transparent';
                console.log(`Reset status for material: ${material.product_name || material.material_name}`);
              }
            });

            // Clear the deselected items array after processing
            this.deselectedPOItems = [];
          }

          // Close modal with updated data to trigger parent reload
          console.log('PO Update successful, closing dialog with updated status');
          const resultData = { status: 'updated', data: response.data };
          console.log('Closing ConfirmPurchaseOrderComponent with:', resultData);
          this.close(resultData);
        } else {
          this.toasterService.showError('Purchase Order', response?.message || 'Failed to update purchase order');
        }
      },
      (error: any) => {
        console.error('Error updating purchase order:', error);
        console.error('Error response:', error?.error);
        const errorMessage = error?.error?.message || error?.message || 'Failed to update purchase order';
        this.toasterService.showError('Purchase Order', errorMessage);
      }
    );
  }

  viewPurchseOrde(item: any) {
    // If PO was successfully created, emit success status before navigating
    if (this.isPONumberGenerated) {
      this.dialogRef.close({ status: 'updated' });
    } else {
      this.dialogRef.close('close');
    }
    this.router.navigate([`purchase-order/detail/${item.po_id}`]);
  }


  generatePONumber() {
    console.log('generatePONumber - vendorGroupList:', this.vendorGroupList);
    console.log('generatePONumber - First vendor materials:', this.vendorGroupList[0]?.materialList);

    // Create PO Payload
    this.vendorGroupList.forEach((x: any) => {
      console.log('📦 Vendor group before payload creation:', {
        name: x.name || x.vendor_name,
        has_is_free_shipping: 'is_free_shipping' in x,
        is_free_shipping_raw: x.is_free_shipping,
        has_is_discount: 'is_discount' in x,
        is_discount_raw: x.is_discount
      });

      let object = {} as CreatePOPayload;
      object.vendor_id = x.vendor_id
      object.po_number = x.po_number
      object.shipping_cost = x.fee_ship
      object.quote_amount = x.quote_amount
      object.is_free_shipping = x.is_free_shipping ?? false  // ← FIX: Default to false
      object.quote_number = String(x.quote_number)
      object.quotation_reference = x.quotation_reference || x.quote_number  // ← NEW: Vendor quote reference
      object.type = this.purchaseOrderObject.type
      object.price_engineering_flag = this.purchaseOrderObject.price_engineering_flag
      object.savings_amount = x.calculation.costSaving || 0
      object.cost_escalation = x.calculation.cost_escalation || 0
      object.subtotal_cost = x.calculation.subTotalCost
      object.shipping_charges = x.calculation.shippingCharges
      object.total_cost = x.calculation.totalCost
      object.is_discount = x.is_discount ?? false  // ← FIX: Default to false
      object.group_name = x.uniqueCode
      object.material_cost = x.calculation.materialCost
      // Stage 6.7: Add vendor tax at PO level
      object.vendor_tax = x.vendor_tax || 0

      console.log('✅ Payload object created:', {
        is_free_shipping: object.is_free_shipping,
        is_discount: object.is_discount,
        vendor_tax: object.vendor_tax
      });

      object.items = x.materialList.map((y: any) => {
        // Calculate the unit cost and contract price properly
        const unitCost = Number(y.unit_price || y.unit_cost || 0);
        const quantity = Number(y.new_quantity || y.quantity || 0);

        // For contract_price: use existing value if available, otherwise calculate from user's edited total
        // Check for both contract_price and po_contract_cost as possible sources
        let contractPrice = 0;
        if (y.contract_price !== undefined && y.contract_price !== null) {
          contractPrice = Number(y.contract_price);
        } else if (y.po_contract_cost !== undefined && y.po_contract_cost !== null) {
          contractPrice = Number(y.po_contract_cost);
        } else if (y.new_quantity_total && quantity > 0) {
          // User edited the total, calculate unit contract price from it
          contractPrice = Number(y.new_quantity_total) / quantity;
        } else {
          // Fallback: calculate from unit cost * quantity
          contractPrice = unitCost * quantity;
        }

        // Calculate total cost: unit cost * quantity
        const totalCost = unitCost * quantity;

        console.log(`Item mapping - contract_price: ${y.contract_price}, po_contract_cost: ${y.po_contract_cost}, calculated contractPrice: ${contractPrice}, totalCost: ${totalCost}`);

        // ✅ CRITICAL FIX: Determine job_scope_item_id
        // API returns job_scope_item_id as 'id' field, need to map it properly
        const jobScopeItemId = y.job_scope_item_id || y.id || null;

        console.log(`🔑 CREATE PO - Material "${y.product_name ||y.material_name}": job_scope_item_id=${y.job_scope_item_id}, id=${y.id}, using=${jobScopeItemId}`);

        return {
          // ✅ Use mapped job_scope_item_id
          job_scope_item_id: jobScopeItemId,
          quantity_on_hand: y.quantity_on_hand || 0,
          order_quantity: quantity,
          total_cost: totalCost,
          scope_id: y.scope_id || null,
          contract_cost: contractPrice,
          unit_cost: unitCost,
          po_contract_cost: this.priceEngineeringFlag ? Number(y.new_quantity_total) : Number(y.previous_quantity_total || y.new_quantity_total || contractPrice),
          // Include all necessary fields for backend
          job_id: y.job_id || null,
          material_id: y.material_id || y.product_id || y.id || null,
          material_name: y.product_name || y.material_name || '',
          material_code: y.material_code || '',
          material_description: y.material_description || '',
          scope: y.scope || y.scope_name || '',
          job_number: y.job_number || '',
          vendor_id: y.vendor_id || null,  // CRITICAL FIX: Send selected vendor_id
          // Stage 6.7: Estimated use tax only (vendor tax is at PO level)
          estimated_use_tax: y.estimated_use_tax || 0
        }
      })
      this.vendorPayloadList.push(object)
    })


    //  Generate PO Payload
    let payloadList: any[] = []
    this.vendorPayloadList.forEach(x => {
      let payloadObject: any = {}
      payloadObject.job_ids = this.jobIdList
      payloadObject.vendor_id = x.vendor_id
      payloadObject.scope_ids = []
      x.items.forEach((y: any) => {
        payloadObject.scope_ids = [y.scope_id]
      })
      payloadList.push(payloadObject)
    })

    // this.backApiService.postApi('purchase/orders/generate-po-number', payloadList).subscribe((response: any) => {
    //   if(response?.success){
    //     this.vendorPayloadList.forEach(x=>{
    //       response.data.forEach((y:any)=>{
    //         if(y.vendor_id==x.vendor_id){
    //           x.po_number=y.po_number
    //         }
    //       })
    //     });

    //   // Step 4: Map response po_number back to vendorGroupList
    //   this.vendorGroupList.forEach((vendor: any) => {
    //     const matched = response.data.find((res: any) => res.vendor_id === vendor.vendor_id);
    //     if (matched) {
    //       vendor.po_number = matched.po_number;
    //     }
    //   });

    //   // Optional: Also update in vendorPayloadList if needed
    //   // this.vendorPayloadList.forEach((vendor: any) => {
    //   //   const matched = response.data.find((res: any) => res.vendor_id === vendor.vendor_id);
    //   //   if (matched) {
    //   //     vendor.po_number = matched.po_number;
    //   //   }
    //   // });
    //   }
    // })
  }

  togglePanel(vendor: any): void {
    vendor.isExpand = !vendor.isExpand;
  }

  /** Total number of items across all vendor groups. */
  getTotalItems(): number {
    return this.vendorGroupList.reduce(
      (sum: number, v: any) => sum + (v.materialList?.length || 0), 0
    );
  }

  /**
   * Amount for one PO group: materials + shipping + vendor tax.
   *
   * This is what the group is submitted as — `total_cost` on the payload is
   * `calculation.totalCost`, i.e. the same three parts (see `generatePONumber`).
   * Single source of truth for the row TOTAL AMOUNT, the expanded panel TOTAL and the
   * footer TOTAL VALUE: the formula used to be inlined in the template twice while the
   * footer summed `quote_amount` alone, so the footer under-reported by the shipping
   * and vendor tax of every group.
   */
  getGroupTotal(group: any): number {
    return (Number(group?.quote_amount) || 0)
      + (Number(group?.fee_ship) || 0)
      + (Number(group?.vendor_tax) || 0);
  }

  /** Total amount across all vendor groups — the sum of the per-group totals listed above. */
  getTotalAmount(): number {
    return this.vendorGroupList.reduce(
      (sum: number, v: any) => sum + this.getGroupTotal(v), 0
    );
  }

}
